import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import {
  useAsset,
  useAssetDocuments,
  useAssetHistory,
} from "../../api/queries/assetQueries";
import { getApiErrorMessage } from "../../utils/apiError";
import { formatPlainDate, formatDateTime } from "../../utils/dateTime";
import {
  AssetStatusBadge,
  AssignmentStatusBadge,
  ConditionText,
} from "./components/AssetBadges";
import AssetActionsMenu from "./components/AssetActionsMenu";
import AssetFormDrawer from "./components/AssetFormDrawer";
import {
  DateReceivedDialog,
  useAssetActions,
} from "./components/AssetActionDialogs";
import { employeeName } from "./components/assetErrors";
import {
  WarrantyText,
  ExpectedReturn,
  AcknowledgementText,
  peso,
} from "./components/AssetDisplay";
import "./assets.css";
import useAssetAccess from "../../hooks/useAssetAccess";

const Meta = ({ label, children }) => (
  <div>
    <div className="approval-meta-label">{label}</div>
    <div className="asset-meta-value">{children || "—"}</div>
  </div>
);

const DOCUMENT_LABEL = {
  RECEIPT: "Receipt",
  WARRANTY: "Warranty",
  ACCOUNTABILITY_FORM: "Accountability form",
  PHOTO: "Photo",
  OTHER: "Other",
};

/**
 * Files already uploaded for the asset (uploading happens in the Assign
 * dialog): read-only links, nothing when there are none.
 */
const DocumentLinks = ({ docs }) =>
  docs.length === 0 ? null : (
    <Meta label={`Documents (${docs.length})`}>
      <ul className="list-unstyled mb-0 d-flex flex-column gap-1">
        {docs.map((d) => (
          <li key={d.id} className="text-truncate">
            <a
              href={d.url}
              target="_blank"
              rel="noopener noreferrer"
              title={d.remarks || d.fileName}
            >
              <i className="bi bi-file-earmark me-1"></i>
              {d.fileName}
            </a>
            {d.category && (
              <span className="fs-070 text-slate-500 ms-1">
                · {DOCUMENT_LABEL[d.category] || d.category}
              </span>
            )}
          </li>
        ))}
      </ul>
    </Meta>
  );

const AssetDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const assetQuery = useAsset(id);
  const historyQuery = useAssetHistory(id);
  const documentsQuery = useAssetDocuments(id);
  const [editing, setEditing] = useState(false);
  const [editingReceived, setEditingReceived] = useState(false);
  const { canModifyAssets } = useAssetAccess();
  const { openAction, dialogs } = useAssetActions({
    onDeleted: () => navigate("/assets"),
  });

  const asset = assetQuery.data;
  const current = asset?.currentAssignment || null;
  const history = historyQuery.data || [];

  const handleAction = (key) => {
    if (key === "edit") return setEditing(true);
    if (key.startsWith("status:"))
      return openAction("status", { asset, status: key.split(":")[1] });
    return openAction(key, { asset, assignment: current || undefined });
  };

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <button
            type="button"
            className="btn btn-sm px-3 fw-semibold d-flex align-items-center profile-btn-white h-35px rounded-8"
            onClick={() =>
              window.history.state?.idx > 0 ? navigate(-1) : navigate("/assets")
            }
          >
            <i className="bi bi-arrow-left me-2"></i> Back
          </button>
          {asset && canModifyAssets && (
            <AssetActionsMenu asset={asset} onAction={handleAction} />
          )}
        </div>

        {assetQuery.isLoading && (
          <div className="text-center py-5">
            <span
              className="spinner-border text-primary"
              role="status"
              aria-label="Loading"
            ></span>
          </div>
        )}
        {assetQuery.isError && (
          <div className="asset-card text-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {getApiErrorMessage(assetQuery.error, "Could not load this asset.")}
          </div>
        )}

        {asset && (
          <div className="row g-3">
            <div className="col-xl-8 d-flex flex-column gap-3">
              {/* Header */}
              <div className="asset-card">
                <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
                  <div>
                    <div className="text-slate-500 fs-080 fw-semibold">
                      {asset.assetTag}
                    </div>
                    <h1 className="fw-bold text-navy fs-150 m-0">
                      {asset.name}
                    </h1>
                    <div className="text-slate-500 fs-085">
                      {[asset.brand, asset.model].filter(Boolean).join(" ") ||
                        "—"}
                    </div>
                  </div>
                  <div className="d-flex align-items-start gap-2">
                    {asset.deletedAt && (
                      <span className="asset-badge asset-badge--red">
                        Deleted
                      </span>
                    )}
                    <AssetStatusBadge status={asset.status} />
                  </div>
                </div>
                <div className="row g-3">
                  <div className="col-6 col-md-3">
                    <Meta label="Type">{asset.assetType?.name}</Meta>
                  </div>
                  <div className="col-6 col-md-3">
                    <Meta label="Condition">
                      <ConditionText condition={asset.condition} />
                    </Meta>
                  </div>
                  <div className="col-6 col-md-3">
                    <Meta label="Branch">
                      {asset.branch?.branchName || "Company level"}
                    </Meta>
                  </div>
                  <div className="col-6 col-md-3">
                    <Meta label="Company">{asset.company?.companyName}</Meta>
                  </div>
                  <div className="col-6 col-md-3">
                    <Meta label="Serial number">{asset.serialNumber}</Meta>
                  </div>
                  <div className="col-12 col-md-9">
                    <Meta label="Specifications">{asset.specifications}</Meta>
                  </div>
                  {asset.remarks && (
                    <div className="col-12">
                      <Meta label="Remarks">{asset.remarks}</Meta>
                    </div>
                  )}
                </div>
              </div>

              {/* Current holder */}
              <div className="asset-card">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                  <h2 className="asset-section-title m-0">Current holder</h2>
                  {current && !asset.deletedAt && canModifyAssets && (
                    <div className="d-flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-primary profile-btn"
                        onClick={() => handleAction("return")}
                      >
                        <i className="bi bi-box-arrow-in-left"></i> Return
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary profile-btn"
                        onClick={() => handleAction("transfer")}
                      >
                        <i className="bi bi-arrow-left-right"></i> Transfer
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger profile-btn"
                        onClick={() => handleAction("lost")}
                      >
                        <i className="bi bi-exclamation-octagon"></i> Mark lost
                      </button>
                    </div>
                  )}
                </div>
                {current ? (
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Meta label="Employee">
                        {employeeName(current.employee)}
                        <div className="text-slate-500 fs-075 fw-normal">
                          {current.employee?.employeeId}
                        </div>
                      </Meta>
                    </div>
                    <div className="col-md-6">
                      <Meta label="Assigned">
                        {formatDateTime(current.assignedAt)}
                      </Meta>
                    </div>
                    <div className="col-md-6">
                      <Meta label="Date received">
                        {current.dateReceived
                          ? formatPlainDate(
                              String(current.dateReceived).slice(0, 10),
                            )
                          : "—"}
                        {!asset.deletedAt && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-2 align-baseline"
                            onClick={() => setEditingReceived(true)}
                          >
                            Change
                          </button>
                        )}
                      </Meta>
                    </div>
                    <div className="col-md-6">
                      <Meta label="Expected return">
                        <ExpectedReturn date={current.expectedReturnDate} />
                      </Meta>
                    </div>
                    <div className="col-md-6">
                      <Meta label="Receipt">
                        <AcknowledgementText
                          acknowledgedAt={current.acknowledgedAt}
                        />
                      </Meta>
                    </div>
                    <div className="col-md-6">
                      <Meta label="Handover condition">
                        <ConditionText condition={current.conditionOut} />
                      </Meta>
                    </div>
                    {current.assignNotes && (
                      <div className="col-md-6">
                        <Meta label="Handover notes">
                          {current.assignNotes}
                        </Meta>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-500 m-0">
                    Not assigned to anyone.
                    {asset.status === "Available" && !asset.deletedAt && (
                      <button
                        type="button"
                        className="btn btn-link p-0 ms-2 align-baseline"
                        onClick={() => handleAction("assign")}
                      >
                        Assign it
                      </button>
                    )}
                  </p>
                )}
              </div>

              {/* Custody history */}
              <div className="asset-card">
                <h2 className="asset-section-title mb-3">Custody history</h2>
                {historyQuery.isLoading && (
                  <span
                    className="spinner-border spinner-border-sm text-primary"
                    role="status"
                    aria-label="Loading"
                  ></span>
                )}
                {!historyQuery.isLoading && history.length === 0 && (
                  <p className="text-slate-500 m-0">
                    This asset has never been assigned.
                  </p>
                )}
                <ol className="custody-timeline">
                  {history.map((h) => (
                    <li
                      key={h.id}
                      className={`custody-item custody-item--${(h.status || "").toLowerCase()}`}
                    >
                      <div className="d-flex flex-wrap justify-content-between gap-2">
                        <div>
                          <span className="fw-bold text-navy">
                            {employeeName(h.employee)}
                          </span>
                          <span className="text-slate-500 fs-075 ms-2">
                            {h.employee?.employeeId}
                          </span>
                        </div>
                        <AssignmentStatusBadge status={h.status} />
                      </div>
                      <div className="fs-080 text-slate-600 mt-1">
                        {formatDateTime(h.assignedAt)} →{" "}
                        {h.returnedAt
                          ? formatDateTime(h.returnedAt)
                          : h.status === "ACTIVE"
                            ? "now"
                            : "—"}
                        {h.expectedReturnDate && (
                          <span className="ms-2">
                            (loan until {formatPlainDate(h.expectedReturnDate)})
                          </span>
                        )}
                        {h.dateReceived && (
                          <span className="ms-2">
                            · received{" "}
                            {formatPlainDate(
                              String(h.dateReceived).slice(0, 10),
                            )}
                          </span>
                        )}
                      </div>
                      <div className="fs-080 mt-1">
                        Condition: <ConditionText condition={h.conditionOut} />
                        {h.conditionIn && (
                          <>
                            {" "}
                            → <ConditionText condition={h.conditionIn} />
                          </>
                        )}
                      </div>
                      {(h.assignNotes || h.returnNotes) && (
                        <div className="fs-080 text-slate-600 mt-1">
                          {h.assignNotes && (
                            <div>
                              <span className="fw-semibold">Out:</span>{" "}
                              {h.assignNotes}
                            </div>
                          )}
                          {h.returnNotes && (
                            <div>
                              <span className="fw-semibold">
                                {h.status === "LOST" ? "Lost:" : "In:"}
                              </span>{" "}
                              {h.returnNotes}
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Purchase & warranty */}
            <div className="col-xl-4">
              <div className="asset-card">
                <h2 className="asset-section-title mb-3">
                  Purchase &amp; warranty
                </h2>
                <div className="d-flex flex-column gap-3">
                  <Meta label="Purchase date">
                    {asset.purchaseDate && formatPlainDate(asset.purchaseDate)}
                  </Meta>
                  <Meta label="Price">{peso(asset.purchasePrice)}</Meta>
                  <Meta label="Supplier">{asset.supplier}</Meta>
                  <Meta label="Warranty expiry">
                    <WarrantyText date={asset.warrantyExpiryDate} />
                  </Meta>
                  <Meta label="Registered">
                    {formatDateTime(asset.createdAt)}
                  </Meta>
                  <DocumentLinks docs={documentsQuery.data || []} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {editing && asset && (
        <AssetFormDrawer asset={asset} onClose={() => setEditing(false)} />
      )}
      {dialogs}
      {editingReceived && current && (
        <DateReceivedDialog
          assignment={current}
          onClose={() => setEditingReceived(false)}
        />
      )}
    </Layout>
  );
};

export default AssetDetailPage;
